import {
    ScmIntegrationsApi,
    scmIntegrationsApiRef,
    ScmAuth,
} from '@backstage/integration-react';
import {
    AnyApiFactory,
    analyticsApiRef,
    configApiRef,
    createApiFactory,
    discoveryApiRef,
    identityApiRef,
} from '@backstage/core-plugin-api';
import {
    ApiBlueprint,
    createFrontendModule,
} from '@backstage/frontend-plugin-api';
import {
    catalogApiRef,
    defaultEntityPresentation,
    entityPresentationApiRef,
} from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { DefaultEntityPresentationApi } from '@backstage/plugin-catalog';
import { IconComponent } from '@backstage/core-plugin-api';
import { SearchApi } from '@backstage/plugin-search-react';
import { searchApi } from '@backstage/plugin-search/alpha';
import DepartmentIcon from '@material-ui/icons/Apartment';
// The icon the Catalog menu gives its Teams entry.
import TeamIcon from '@material-ui/icons/People';
// The icon of a group in general - an Entra group, a security group - the
// way the page of a user shows their Entra groups. A department and a team
// have icons of their own.
import EntraGroupIcon from '@material-ui/icons/VpnKey';
import ClusterIcon from '@material-ui/icons/Widgets';
import ClusterSliceIcon from '@material-ui/icons/DonutSmall';
import CloudAccountIcon from '@material-ui/icons/Cloud';
import ProductIcon from '@material-ui/icons/Archive';
import { techRadarApiRef } from '@backstage-community/plugin-tech-radar';
import {
    AirflowIcon,
    EntityPresentationWithVuid,
    ModelClient,
    TechRadarClient,
    StatisticClient,
    modelApiRef,
    vuidOf,
} from '@internal/plugin-rtl-data';

export const apis: AnyApiFactory[] = [
    createApiFactory({
        api: scmIntegrationsApiRef,
        deps: { configApi: configApiRef },
        factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
    }),
    ScmAuth.createDefaultApiFactory(),
    createApiFactory({
        api: techRadarApiRef,
        deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef },
        factory: ({ identityApi, discoveryApi }) =>
            new TechRadarClient(identityApi, discoveryApi),
    }),
    createApiFactory({
        api: analyticsApiRef,
        deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef },
        factory: ({ identityApi, discoveryApi }) =>
            StatisticClient.fromConfig(identityApi, discoveryApi),
    }),
    createApiFactory({
        api: modelApiRef,
        deps: { discoveryApi: discoveryApiRef, identityApi: identityApiRef },
        factory: ({ identityApi, discoveryApi }) =>
            new ModelClient(identityApi, discoveryApi),
    }),
];

/**
 * The icon of an entity the kind alone does not tell apart: a department, a
 * team and an Entra group are all Groups, a Kubernetes cluster and one of its slices are both
 * Resources, and the stock presentation picks its icon by kind, so each pair
 * looks the same wherever an entity is linked. A cloud account of any vendor
 * gets the one cloud: the vendor is in its name and in its type already, and
 * their logos are not ours to vendor.
 *
 * A Product needs nothing here, its kind alone tells it apart - `kindIcons`
 * below gives it its icon.
 *
 * Anything else keeps the kind icon of the default renderer.
 *
 * By kind and type rather than by entity, so that the Catalog menu shows a
 * type with the icon its entities are linked with.
 */
export function iconOfType(
    kind: string,
    type: unknown,
): IconComponent | undefined {
    switch (`${kind.toLocaleLowerCase('en-US')}/${type}`) {
        case 'group/department':
            return DepartmentIcon;
        case 'group/team':
            return TeamIcon;
        case 'group/ad-group':
            return EntraGroupIcon;
        case 'resource/k8s-cluster':
            return ClusterIcon;
        case 'resource/k8s-cluster-slice':
            return ClusterSliceIcon;
        case 'resource/aws-account':
        case 'resource/azure-subscription':
        case 'resource/gcp-project':
        case 'resource/oci-account':
            return CloudAccountIcon;
        // The logo the app shows next to Airflow links already.
        case 'resource/airflow':
            return AirflowIcon;
        default:
            return undefined;
    }
}

/** See {@link iconOfType}. */
function iconOf(entity: Entity): IconComponent | undefined {
    return iconOfType(entity.kind, entity.spec?.type);
}

/**
 * How much has to be typed before anything is asked of the search backend. The
 * index holds some 14k documents, and a term of one or two letters matches so
 * many of them that the first page is noise - the empty term that the search
 * page and the sidebar popup start out with matches all of them, which is the
 * most expensive query there is.
 */
export const MIN_SEARCH_TERM_LENGTH = 3;

/**
 * The search api with that rule in front of it: a term below the limit is
 * answered with an empty result set instead of a request. This sits in the api
 * rather than in the search bar because the search context queries on its own -
 * once on mount and again on every filter change - so a gate in the input
 * would let exactly the queries through that are worth stopping.
 *
 * Exported for the sake of its test; the module below is the only caller.
 */
export function gateShortTerms(delegate: SearchApi): SearchApi {
    return {
        query: (query, options) =>
            query.term.trim().length < MIN_SEARCH_TERM_LENGTH
                ? Promise.resolve({ results: [], numberOfResults: 0 })
                : delegate.query(query, options),
    };
}

/**
 * The search plugin brings its own client as `api:search`, and it is not
 * exported, so the gate is wrapped around whatever that extension builds rather
 * than replacing it - the app keeps upstream's client and only decides which
 * terms reach it.
 */
export const searchApiModule = createFrontendModule({
    pluginId: 'search',
    extensions: [
        searchApi.override({
            factory(originalFactory) {
                const original = originalFactory().get(
                    ApiBlueprint.dataRefs.factory,
                );
                return [
                    ApiBlueprint.dataRefs.factory({
                        ...original,
                        factory: deps =>
                            gateShortTerms(original.factory(deps) as SearchApi),
                    }),
                ];
            },
        }),
    ],
});

/**
 * What every link to an entity is made of: the icon of {@link iconOf} where
 * the kind alone does not say which thing it is, and the vuid of `vuidOf` for
 * every entity the CMDB gives one.
 *
 * The vuid is not written into the title. It travels next to it, so that
 * wherever there is room it is shown as a chip that copies it, see
 * `EntityRefLinkWithVuid` - and the title stays the name everywhere else, in
 * the header of a page, in a graph or a dropdown.
 *
 * Exported for the sake of its test; the api below is the only caller.
 */
export function renderEntityPresentation(request: {
    entityRef: string;
    entity?: Entity;
    context?: { defaultKind?: string; defaultNamespace?: string };
}): { snapshot: EntityPresentationWithVuid } {
    const { entityRef, entity, context } = request;
    const snapshot = defaultEntityPresentation(entity ?? entityRef, context);
    // `entity` is undefined until the catalog answers, the kind icon of the
    // default renderer covers that frame.
    if (!entity) {
        return { snapshot: snapshot };
    }
    const vuid = vuidOf(entity);
    const Icon = iconOf(entity);
    return {
        snapshot: {
            ...snapshot,
            ...(Icon ? { Icon: Icon } : {}),
            ...(vuid ? { vuid: vuid } : {}),
        },
    };
}

/**
 * Product is not a kind the stock presentation knows at all, so it ends up
 * with no icon where every other kind has one - `kindIcons` fills that in with
 * the same icon the sidebar uses for products. A group gets the key instead
 * of the stock people, which the teams have, see {@link iconOfType}.
 *
 * The catalog plugin already brings this api as `api:catalog/entity-
 * presentation`, so it has to be overridden under that same id - a second one
 * in `apis` above would collide with it at startup.
 */
export const entityPresentationModule = createFrontendModule({
    pluginId: 'catalog',
    extensions: [
        ApiBlueprint.make({
            name: 'entity-presentation',
            params: defineParams =>
                defineParams({
                    api: entityPresentationApiRef,
                    deps: { catalogApi: catalogApiRef },
                    factory: ({ catalogApi }) =>
                        DefaultEntityPresentationApi.create({
                            catalogApi,
                            kindIcons: {
                                product: ProductIcon,
                                group: EntraGroupIcon,
                            },
                            renderer: {
                                // Without this no loader is built at all and
                                // a ref never gets its entity, which leaves
                                // every link showing the bare ref instead of
                                // the title or display name.
                                async: true,
                                render: renderEntityPresentation,
                            },
                        }),
                }),
        }),
    ],
});
